import { z } from 'zod'

const baseSchema = z.object({ id: z.string(), type: z.string() })

const statusReportBaseSchema = z.object({
    title: z.string(),
    starts_at: z.string(),
    aggregate_state: z.enum(['resolved', 'downtime', 'degraded', 'maintenance']),
})

const statusReportSchema = z.object({
    id: z.string(),
    type: z.literal('status_report'),
    attributes: z.discriminatedUnion('report_type', [
        statusReportBaseSchema.extend({ report_type: z.literal('manual'), ends_at: z.string().nullable() }),
        statusReportBaseSchema.extend({ report_type: z.literal('automatic'), ends_at: z.string().nullable() }),
        statusReportBaseSchema.extend({ report_type: z.literal('maintenance'), ends_at: z.string() }),
    ]),
    relationships: z.object({
        status_updates: z.object({
            data: z.array(baseSchema),
        }),
    }),
})

const statusUpdateSchema = z.object({
    id: z.string(),
    type: z.literal('status_update'),
    attributes: z.object({
        message: z.string(),
        published_at: z.string(),
    }),
})

export type StatusReport = z.infer<typeof statusReportSchema>
export type StatusUpdate = z.infer<typeof statusUpdateSchema>
export type StatusReportOrStatusUpdate = StatusReport | StatusUpdate

export const betterstackStatusPageResponseSchema = z.object({
    included: z
        .array(baseSchema.loose())
        .nullish()
        .transform(items =>
            (items ?? []).flatMap<StatusReportOrStatusUpdate>(item => {
                if (item.type === 'status_report') return [statusReportSchema.parse(item)]
                if (item.type === 'status_update') return [statusUpdateSchema.parse(item)]
                return [] // Drop unknown types as these are unused
            })
        ),
})

export type UnresolvedAggregateState = Exclude<StatusReport['attributes']['aggregate_state'], 'resolved'>
export type UnresolvedStatusReport = StatusReport & { attributes: { aggregate_state: UnresolvedAggregateState } }
export type IncidentStatusReport = UnresolvedStatusReport & { attributes: { report_type: 'manual' | 'automatic' } }
export type MaintenanceStatusReport = UnresolvedStatusReport & { attributes: { report_type: 'maintenance'; ends_at: string } }
export type IncidentOrMaintenanceStatusReport = IncidentStatusReport | MaintenanceStatusReport

export const isUnresolvedStatusReport = (item: StatusReportOrStatusUpdate): item is UnresolvedStatusReport =>
    item.type === 'status_report' && item.attributes.aggregate_state !== 'resolved'

export const isIncidentStatusReport = (item: UnresolvedStatusReport): item is IncidentStatusReport =>
    item.attributes.report_type === 'manual' || item.attributes.report_type === 'automatic'

export const isMaintenanceStatusReport = (report: UnresolvedStatusReport): report is MaintenanceStatusReport => report.attributes.report_type === 'maintenance'

export const isStatusUpdate = (item: StatusReportOrStatusUpdate): item is StatusUpdate => item.type === 'status_update'
